import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { isMobileSelector } from 'redux/selectors/global';
import Typography from 'components/core/Typography/Typography';
import Icon, { IconName } from 'components/core/Icon/Icon';
import { Typographies } from 'models/uiElements';
import { CDN_IMAGES_URL } from 'consts';
import { FontWeights } from 'models';

const DemoAuthPayment = () => {
  const isMobile = useSelector(isMobileSelector);

  const titles = {
    title: '3D Secure Payment Authentication',
    subtitle:
      'For enhanced security, certain transactions may involve 3D Secure, prompting customers to complete additional verification through methods like push notifications, text messages, or alternative method specified by their bank.'
  };

  return (
    <StyledModalWrapper>
      {!isMobile && <StyledIcon src={`${CDN_IMAGES_URL}/auth-payment.svg`} />}
      <Title name="demo-modal-title" type={Typographies.H5_HEADING}>
        {titles.title}
      </Title>
      <SubTitle name="demo-modal-subtitle" type={Typographies.BODY1}>
        {titles.subtitle}
      </SubTitle>
      <CasesWrapper>
        <Case>
          <CaseHeader>
            <SuccessIcon name={IconName.ACCEPT}></SuccessIcon>
            <CaseTitle type={Typographies.SUBTITLE}>Success</CaseTitle>
          </CaseHeader>
          <CaseBody>
            <CaseDescription type={Typographies.BODY1}>
              Upon successful payment, the customer will be redirected back to your
              website.
            </CaseDescription>
          </CaseBody>
        </Case>
        <Case>
          <CaseHeader>
            <ErrorIcon name={IconName.WARNING_2}></ErrorIcon>
            <CaseTitle type={Typographies.SUBTITLE}>Failure</CaseTitle>
          </CaseHeader>
          <CaseBody>
            <CaseDescription type={Typographies.BODY1}>
              Payment authentication failure prompts the customer to retry or select an
              alternative payment method.
            </CaseDescription>
          </CaseBody>
        </Case>
      </CasesWrapper>
    </StyledModalWrapper>
  );
};

export default DemoAuthPayment;

const StyledModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }) => theme.colors.grey.grey100};
`;

const StyledIcon = styled.img`
  width: 10.8rem;
  height: 10.8rem;
  margin-bottom: 2rem;
`;

const Title = styled(Typography)`
  display: flex;
  justify-content: center;
  text-align: center;
  height: ${({ theme }) => (theme.view.isMobile ? '6.5rem' : '4rem')};
  font-weight: ${FontWeights.MEDIUM};
  color: ${({ theme }) => theme.colors.secondaryText};
`;

const SubTitle = styled(Typography)`
  display: block;
  text-align: center;
  max-width: 63.5rem;
  padding: ${({ theme }) =>
    theme.view.isMobile ? '0 2rem 2rem 2rem' : '0 10rem 1rem 10rem'};
`;

const CasesWrapper = styled.div`
  margin-bottom: ${({ theme }) => (theme.view.isMobile ? '2rem' : '4rem')};
  max-width: ${({ theme }) => (theme.view.isMobile ? '30rem' : '40rem')};
`;

const Case = styled.div`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => (theme.view.isMobile ? '2.5rem' : '1rem')};
  }
`;

const CaseHeader = styled.div`
  display: flex;
  align-items: baseline;
  padding-top: ${({ theme }) => (theme.view.isMobile ? '1.5rem' : '2rem')};
`;

const caseIconStyle = css`
  margin-right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
`;

const SuccessIcon = styled(Icon)`
  ${caseIconStyle};
  color: ${({ theme }) => theme.colors.success};
`;

const ErrorIcon = styled(Icon)`
  ${caseIconStyle};
  color: ${({ theme }) => theme.colors.error};
`;

const CaseTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.secondaryText};
`;

const CaseBody = styled.div`
  padding: 0.5rem 0 0 2rem;
`;

const CaseDescription = styled(Typography)`
  color: ${({ theme }) => theme.colors.surface1};
`;
